import * as React from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'
import { QUERY_IDEAS_COUNT } from '~publish/pages/Create/hooks/useIdeasCount'
import type { Idea } from '~publish/pages/Create/types'

export const DeleteIdeaMutation = graphql(/* GraphQL */ `
  mutation DeleteIdeaMutation($input: IdeasDeletionInput!) {
    ideasRemove(input: $input) {
      ... on IdeasDeletionSuccess {
        success
        message
        ideas {
          id
        }
      }
      ... on CoreWebAppCommonError {
        message
      }
    }
  }
`)

type DeleteIdeaOptions = {
  source?: string
}

function useDeleteIdea(): {
  deleteIdea: (
    idea: Pick<Idea, 'id'>,
    options?: DeleteIdeaOptions,
  ) => Promise<void>
  deleteIdeas: (ideaIds: string[], options?: DeleteIdeaOptions) => Promise<void>
} {
  const [deleteMutation] = useMutation(DeleteIdeaMutation, {
    // Refetch the idea count so limit-reached UI and create affordances
    // reflect the freed capacity immediately, without a page reload.
    refetchQueries: [QUERY_IDEAS_COUNT],
    update: (cache, { data }, { variables }) => {
      if (
        !data ||
        !data.ideasRemove ||
        !variables ||
        data.ideasRemove.__typename !== 'IdeasDeletionSuccess'
      ) {
        return
      }

      // evict ideas from cache
      for (const idea of data.ideasRemove.ideas) {
        const id = cache.identify({
          id: idea.id,
          __typename: 'Idea',
        })
        cache.evict({ id })
      }

      cache.gc()
    },
  })

  const deleteIdeas = React.useCallback(
    async (ideaIds: string[], options?: DeleteIdeaOptions): Promise<void> => {
      const { data, error } = await deleteMutation({
        variables: {
          input: {
            ids: ideaIds,
            cta: options?.source,
          },
        },
      })

      if (!data || !data.ideasRemove) {
        throw new Error('No data returned from server')
      }

      if (data.ideasRemove.__typename === 'IdeasDeletionSuccess') {
        return
      }

      if (data.ideasRemove.message) {
        throw new Error(data.ideasRemove.message)
      }

      const unexpectedError = new Error(
        error?.message || 'Unexpected API response',
      )

      if (error) {
        logError(unexpectedError, { metaData: { data, error } })
      }

      throw unexpectedError
    },
    [deleteMutation],
  )

  const deleteIdea = React.useCallback(
    async (
      idea: Pick<Idea, 'id'>,
      options?: DeleteIdeaOptions,
    ): Promise<void> => {
      return deleteIdeas([idea.id], options)
    },
    [deleteIdeas],
  )

  return { deleteIdea, deleteIdeas }
}

export { useDeleteIdea }
